import type { NotificationItem } from '@mrb91002/shared-types'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { fetchNotificationsPaginated, markNotificationRead } from '../api/secureRequests'
import { useAuth } from '../context/AuthContext'
import { useUserRelations } from '../context/UserRelationsContext'
import { getNotificationIcon } from './pages/NotificationCenter/notificationUtils'

import { Popover } from '@/components/ui/base'
import { Button } from '@/components/ui/base'
import { Box } from '@/components/ui/base'

const NotificationBell: React.FC = () => {
  const { unreadNotificationCount } = useUserRelations()
  const { idToken } = useAuth()
  const navigate = useNavigate()

  const [allNotifications, setAllNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Fetch first page of all notifications on mount (when idToken is available)
  useEffect(() => {
    if (!idToken || loaded) return
    setLoading(true)
    fetchNotificationsPaginated(null, idToken)
      .then((data) => {
        setAllNotifications(data.notifications ?? [])
        setLoaded(true)
      })
      .catch((err) => console.error('[NotificationBell] Failed to load notifications:', err))
      .finally(() => setLoading(false))
  }, [idToken, loaded])

  // Show the 5 most recent unread notifications
  const unreadNotifications = allNotifications.filter((n) => !n.read).slice(0, 5)

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    return `${Math.floor(diffInMinutes / 1440)}d ago`
  }

  const handleMarkAsRead = async (notification: NotificationItem) => {
    if (!idToken || notification.read) return
    try {
      await markNotificationRead(notification.id, notification.category, notification.created_at_ms, idToken)
      setAllNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)))
    } catch (err: unknown) {
      console.error('[NotificationBell] Failed to mark as read:', err)
    }
  }

  const handleActionClick = (action: { url: string }, notification: NotificationItem) => {
    handleMarkAsRead(notification)
    navigate(action.url)
  }

  return (
    <Popover
      position="bottom"
      size="medium"
      triggerType="custom"
      dismissButton={false}
      content={
        <Box>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Box variant="h3" margin={{ bottom: 'none' }}>
              Notifications
            </Box>
            <Button variant="link" onClick={() => navigate('/notifications')} fontSize="12px">
              View all
            </Button>
          </div>
          <div style={{ minWidth: 320, maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <Box padding="m" color="inherit">
                Loading…
              </Box>
            ) : unreadNotifications.length === 0 ? (
              <Box padding="m" color="inherit">
                No unread notifications
              </Box>
            ) : (
              <>
                {unreadNotifications.map((notification) => (
                  <Box
                    key={notification.id}
                    padding="s"
                    margin={{ bottom: 'xxs' }}
                    backgroundColor="grey-100"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleMarkAsRead(notification)}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: '100%' }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{getNotificationIcon(notification.type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            marginBottom: 2,
                            fontSize: 14,
                          }}
                        >
                          {notification.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#666',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            marginBottom: 4,
                          }}
                        >
                          {notification.preview}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: '#999' }}>{formatTimeAgo(notification.created_at)}</span>
                          <span style={{ color: '#3b82f6', fontSize: 12 }}>•</span>
                        </div>
                        {notification.actions && notification.actions.length > 0 && (
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {notification.actions.slice(0, 2).map((action, index) => (
                              <button
                                key={index}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  border: '1px solid #d1d5db',
                                  borderRadius: 4,
                                  background: action.style === 'primary' ? 'rgb(59, 130, 135)' : 'white',
                                  color: action.style === 'primary' ? 'white' : '#374151',
                                  cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleActionClick(action, notification)
                                }}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Box>
                ))}
                <Box padding="s" textAlign="center">
                  <Button onClick={() => navigate('/notifications')} variant="link" fontSize="12px">
                    View all notifications
                  </Button>
                </Box>
              </>
            )}
          </div>
        </Box>
      }
    >
      <div style={{ position: 'relative' }}>
        <div style={{ color: '#3B8287', display: 'inline-block' }}>
          <button
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', fontSize: '20px' }}
            aria-label="Notifications"
          >
            🔔
          </button>
        </div>
        {unreadNotificationCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              fontSize: 11,
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
          </span>
        )}
      </div>
    </Popover>
  )
}

export default NotificationBell
